/**
 * @typedef {import('./types').throttle} throttle
 * @typedef {import('./types').debounce} debounce
 * @typedef {import('./types').setCookie} setCookie
 * @typedef {import('./types').getCookie} getCookie
 * @typedef {import('./types').updateCartStateDisplays} updateCartStateDisplays
 * @typedef {import('./types').AnimatedDetails} AnimatedDetails
 */

/** @type {throttle} */
function throttle(fn, timeout) {
  let ready = true;

  return (...args) => {
    if (!ready) return;

    ready = false;

    fn(...args);

    setTimeout(() => {
      ready = true;
    }, timeout);
  };
}

/** @type {debounce} */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout>} */
  let timeout;

  return (...args) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      fn(...args);
    }, wait);
  };
}

/** @type {setCookie} */
function setCookie(name, value, hours) {
  if (!name || typeof hours !== 'number') return;

  const date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);

  const expires = `expires=${date.toUTCString()}`;

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
}

/** @type {getCookie} */
function getCookie(name) {
  if (!name) return null;

  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(nameEQ)) {
      return trimmed.substring(nameEQ.length);
    }
  }

  return null;
}

/** @type {updateCartStateDisplays} */
async function updateCartStateDisplays(count = undefined, total = undefined) {
  const counters = Array.from(document.querySelectorAll('[data-cart-item-count]'));
  const totals = Array.from(document.querySelectorAll('[data-cart-total]'));

  /** @param {string} count - The number of items in the cart */
  function setCartItemCount(count) {
    counters.forEach((c) => {
      c.setAttribute('data-cart-item-count', count);
      c.textContent = count;
    });
  }

  /** @param {string} total - The total price of the cart */
  function setCartTotal(total) {
    totals.forEach((t) => {
      t.setAttribute('data-cart-total', total);
      t.textContent = total;
    });
  }

  /** @param {string} count - The number of items in the cart */
  function setCheckoutButtons(count) {
    const checkouts = document.querySelectorAll('[name="checkout"]');

    const int = Number(count);

    checkouts.forEach((c) => {
      if (int > 0) {
        c.removeAttribute('disabled');
      } else {
        c.setAttribute('disabled', '');
      }
    });
  }

  if (counters.length > 0 && count && totals.length > 0 && total) {
    setCartTotal(total);
    setCartItemCount(count);
    setCheckoutButtons(count);

    return;
  }

  const cartSectionUrl =
    location.pathname.endsWith('/cart') || location.pathname.endsWith('/cart/')
      ? `${window.Shopify.routes.root}cart?sections=section-main-cart`
      : `${window.Shopify.routes.root}?sections=section-main-cart`;
  const request = await fetch(cartSectionUrl);

  if (!request.ok) {
    throw new Error(request.statusText);
  }

  const data = await request.json();

  if (data['section-main-cart']) {
    const html = new DOMParser().parseFromString(data['section-main-cart'], 'text/html');
    const component = html.querySelector('cart-form');

    if (component && component instanceof HTMLElement) {
      const total = component.dataset.cartTotalValue?.replace('<span class=money>', '').replace('</span>', '');
      const quantity = component.dataset.cartQuantityValue;

      if (total && quantity) {
        setCartTotal(total);
        setCartItemCount(quantity);
        setCheckoutButtons(quantity);
      }
    }
  }
}

/**
 * @param {KeyboardEvent} event - The first keyboard event that indicates keyboard navigation.
 */
function onFirstKeyboardNavigation(event) {
  if (
    event.key !== 'Tab' &&
    event.key !== 'ArrowDown' &&
    event.key !== 'ArrowUp' &&
    event.key !== 'ArrowLeft' &&
    event.key !== 'ArrowRight'
  )
    return;

  if (!document.body) return;

  document.body.classList.add('user-interacted');

  window.removeEventListener('keydown', onFirstKeyboardNavigation, true);
}

window.addEventListener('keydown', onFirstKeyboardNavigation, true);

window.traps = {
  create: (target, options) => {
    if (target) {
      const id = target.dataset.focusTrap;

      if (id) {
        const trap = window.focusTrap.createFocusTrap(target, options);

        if (trap) {
          window.traps.store[id] = trap;

          return window.traps.store[id];
        }
      }
    }
  },
  deactivate: (excluded) => {
    Object.values(window.traps.store).forEach((trap) => {
      if (excluded) {
        if (!excluded.includes(trap)) {
          trap.deactivate();
        }
      } else {
        trap.deactivate();
      }
    });
  },
  store: {},
};

addEventListener('click', (e) => {
  const target = e.target;

  if (target && target instanceof Element) {
    const data = target.closest('[data-focus-trap-toggle]');

    if (data && data instanceof HTMLElement) {
      const id = data.dataset.focusTrapToggle;

      if (id && window.traps.store[id]) {
        const trap = window.traps.store[id];

        if (!trap.active) {
          if (!target.parentElement?.closest('[data-focus-trap]')) {
            window.traps.deactivate();
          }

          trap.activate();
        } else {
          trap.deactivate();
        }
      }
    }
  }
});

if (!customElements.get('modal-trap')) {
  customElements.define(
    'modal-trap',
    class ModalTrap extends HTMLElement {
      constructor() {
        super();
        this.trap = undefined;

        /** @type {HTMLElement | null} */
        this.modal = this.querySelector('[data-focus-trap]');

        /** @type {HTMLButtonElement | null} */
        this.fallback = this.querySelector('[data-focus-trap-fallback]');

        /** @type {string[]} */
        this.tabs = [];
      }

      connectedCallback() {
        if (this.modal) {
          const id = this.modal.dataset.focusTrap;

          this.tabs = Array.from(this.modal.querySelectorAll('[data-tab-id]'))
            .map((button) => {
              if (button instanceof HTMLElement && button.dataset.tabId) {
                return button.dataset.tabId;
              }

              return '';
            })
            .filter(Boolean);

          if (id && !window.traps.store[id]) {
            this.trap = window.traps.create(this.modal, {
              onActivate: () => {
                // Move the modal trap to the end of the body to ensure it's at the top of the stacking context
                document.body.append(this);

                const event = new CustomEvent('modal-trap-activate', {
                  detail: {
                    id,
                    trap: this.trap,
                  },
                });

                this.modal?.setAttribute('aria-expanded', 'true');

                if (this.hasAttribute('data-prevent-freeze')) {
                  if (this.dataset.preventFreeze === 'mobile' && window.innerWidth >= 768) {
                    document.body.setAttribute('data-frozen-scroll', 'true');
                  } else if (this.dataset.preventFreeze === 'desktop' && window.innerWidth < 768) {
                    document.body.setAttribute('data-frozen-scroll', 'true');
                  }
                } else {
                  document.body.setAttribute('data-frozen-scroll', 'true');
                }

                window.dispatchEvent(event);
              },
              onDeactivate: () => {
                const event = new CustomEvent('modal-trap-deactivate', {
                  detail: {
                    id,
                    trap: this.trap,
                  },
                });

                this.modal?.setAttribute('aria-expanded', 'false');

                window.dispatchEvent(event);
              },

              onPostDeactivate: () => {
                if (!Object.values(window.traps.store).some((trap) => trap.active)) {
                  document.body.removeAttribute('data-frozen-scroll');
                }
              },
              fallbackFocus: this.fallback ?? undefined,
              clickOutsideDeactivates: this.getAttribute('data-click-outside-deactivates') !== 'false',
              escapeDeactivates: this.getAttribute('data-escape-deactivates') !== 'false',
            });
          }
        }
      }
    },
  );
}

if (!customElements.get('cookie-consent')) {
  customElements.define(
    'cookie-consent',
    class CookieConsent extends HTMLElement {
      constructor() {
        super();

        /** @type {string} */
        this.handle = this.querySelector('[data-focus-trap]')?.getAttribute('data-focus-trap') || 'cookies';

        this.trap = window.traps.store[this.handle];

        /** @type {HTMLInputElement | null} */
        this.settingAnalytics = this.querySelector('[data-cookies-accept-analytics]');

        /** @type {HTMLInputElement | null} */
        this.settingMarketing = this.querySelector('[data-cookies-accept-marketing]');

        /** @type {HTMLInputElement | null} */
        this.settingPreferences = this.querySelector('[data-cookies-accept-preferences]');

        /** @type {HTMLInputElement | null} */
        this.acceptAll = this.querySelector('[data-cookies-accept-all]');

        /** @type {HTMLInputElement | null} */
        this.rejectAll = this.querySelector('[data-cookies-reject-all]');

        /** @type {HTMLButtonElement | null} */
        this.save = this.querySelector('[data-cookies-save]');

        /** @type {HTMLDivElement | null} */
        this.actions = this.querySelector('[data-cookies-actions]');

        /** @type {HTMLButtonElement | null} */
        this.expand = this.querySelector('[data-cookies-expand]');

        /** @type {HTMLButtonElement | null} */
        this.collapse = this.querySelector('[data-cookies-collapse]');

        /** @type {AnimatedDetails | null} */
        this.details = this.querySelector('animated-details');

        this.handleExpandClick = this.handleExpandClick.bind(this);
        this.handleCollapseClick = this.handleCollapseClick.bind(this);
        this.handleExpandTransitionEnd = this.handleExpandTransitionEnd.bind(this);

        this.listenersBound = false;
      }

      connectedCallback() {
        window.consent = {
          marketing: '',
          analytics: '',
          preferences: '',
          sale_of_data: '',
        };

        window.Shopify.loadFeatures(
          [
            {
              name: 'consent-tracking-api',
              version: '0.1',
            },
          ],
          async () => {
            const consent = await window.Shopify.customerPrivacy.currentVisitorConsent();

            window.consent = {
              marketing: consent.marketing,
              analytics: consent.analytics,
              preferences: consent.preferences,
              sale_of_data: consent.sale_of_data,
            };

            window.gtag('consent', 'update', {
              ad_storage: window.consent.marketing === 'yes' ? 'granted' : 'denied',
              analytics_storage: window.consent.analytics === 'yes' ? 'granted' : 'denied',
              ad_user_data: window.consent.marketing === 'yes' ? 'granted' : 'denied',
              ad_personalization: window.consent.marketing === 'yes' ? 'granted' : 'denied',
            });

            if (this.shouldShowBanner() && this.trap) this.trap.activate();

            if (this.save) this.save.addEventListener('click', () => this.setConsent());

            if (this.acceptAll) this.acceptAll.addEventListener('click', () => this.setAcceptAll());

            if (this.rejectAll) this.rejectAll.addEventListener('click', () => this.setRejectAll());

            if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
              this.settingMarketing.checked = !!window.consent.marketing;
              this.settingAnalytics.checked = !!window.consent.analytics;
              this.settingPreferences.checked = !!window.consent.preferences;
            }
          },
        );

        if (!this.listenersBound && this.actions && this.expand && this.collapse && this.details) {
          this.expand.addEventListener('click', this.handleExpandClick);
          this.collapse.addEventListener('click', this.handleCollapseClick);
          this.expand.addEventListener('transitionend', this.handleExpandTransitionEnd);

          this.listenersBound = true;
        }

        document.addEventListener('visitorConsentCollected', () => {
          /** @type {import('./types').LocalizationBanner | null} */
          const localization = document.querySelector('localization-banner');

          if (
            localization &&
            !!localization.suggestion &&
            localStorage.getItem('viewed-localization-banner') !== 'true'
          ) {
            localization.trap?.activate();
          }
        });
      }

      handleExpandClick() {
        if (!this.actions || !this.details) return;

        this.actions.setAttribute('data-expanded', 'true');
        this.details.toggle();

        const fallbackFocusElement = this.querySelector('[data-cookies-option]');

        if (fallbackFocusElement && fallbackFocusElement instanceof HTMLElement) {
          fallbackFocusElement.focus();
        }
      }

      handleCollapseClick() {
        if (!this.actions || !this.details || !this.expand) return;

        this.actions.removeAttribute('data-expanded');
        this.details.toggle();
        this.expand.removeAttribute('data-hidden');
        this.expand.focus();
      }

      handleExpandTransitionEnd() {
        if (this.actions?.hasAttribute('data-expanded')) {
          this.expand?.setAttribute('data-hidden', 'true');
        }
      }

      setConsent() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          this.dataset.loading = 'true';
          const settings = {
            marketing: this.settingMarketing.checked,
            analytics: this.settingAnalytics.checked,
            preferences: this.settingPreferences.checked,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      setAcceptAll() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          this.dataset.loading = 'true';

          const settings = {
            marketing: true,
            analytics: true,
            preferences: true,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      setRejectAll() {
        if (this.settingMarketing && this.settingAnalytics && this.settingPreferences) {
          this.dataset.loading = 'true';
          const settings = {
            marketing: false,
            analytics: false,
            preferences: false,
            sale_of_data: window.Shopify.customerPrivacy.saleOfDataAllowed(),
          };

          window.Shopify.customerPrivacy.setTrackingConsent(settings, () => {
            window.consent = {
              marketing: settings.marketing ? 'yes' : 'no',
              analytics: settings.analytics ? 'yes' : 'no',
              preferences: settings.preferences ? 'yes' : 'no',
              sale_of_data: settings.sale_of_data ? 'yes' : 'no',
            };

            if (this.trap) this.trap.deactivate();
          });
        }
      }

      shouldShowBanner() {
        if (location.hostname === '127.0.0.1' || !!window.Shopify.designMode) return false;

        return Object.values(window.consent).some((value) => value === '');
      }
    },
  );
}

/**
 * Reverts a successful cart/add.js when the subsequent line removal fails.
 * @param {{ items?: Array<{ key: string; variant_id: number; quantity: number }> }} cartData - The cart response from cart/add.js
 * @param {number} newVariantId - The variant ID that was added
 * @param {number} quantity - The quantity that was added
 * @param {string} oldLineKey - The line item key being replaced
 * @returns {Promise<{ item_count: number } | null>} - Updated cart state after rollback, or null on failure
 */
async function rollbackVariantSizeChange(cartData, newVariantId, quantity, oldLineKey) {
  const addedLine = cartData.items?.find((item) => item.variant_id === newVariantId && item.key !== oldLineKey);

  if (!addedLine) return null;

  try {
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);

    if (!cartResponse.ok) return null;

    /** @type {{ items?: Array<{ key: string; quantity: number }> }} */
    const cart = await cartResponse.json();
    const currentLine = cart.items?.find((item) => item.key === addedLine.key);

    if (!currentLine) return null;

    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: addedLine.key,
        quantity: Math.max(0, currentLine.quantity - quantity),
      }),
    });

    if (!response.ok) return null;

    return response.json();
  } catch {
    return null;
  }
}

if (!customElements.get('cart-form')) {
  customElements.define(
    'cart-form',
    class CartForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.controller = null;
        this.variantController = null;
        this.isVariantChanging = false;
        this.cartOperationId = 0;
        this.cartOperationSeq = 0;
        this.variantOperationId = 0;
        this.variantOperationSeq = 0;
        this.cartStateJson = this.querySelector('[data-cart-state-json]')?.textContent || '{}';
      }

      connectedCallback() {
        this.emitCartUpdatedEvent();

        if (this.form) {
          this.form.addEventListener('change', this.changeHandler.bind(this));
          this.form.addEventListener('click', this.clickHandler.bind(this));
        }
      }

      emitCartUpdatedEvent() {
        const state = JSON.parse(this.cartStateJson);
        const signature = JSON.stringify(state);

        if (window.__cartStateSignature === signature) return;

        window.__cartStateSignature = signature;

        window.dispatchEvent(new CustomEvent('cart-updated', { detail: state }));
      }

      /** @param {boolean} locked - Whether cart form interactions should be locked */
      setFormLocked(locked) {
        if (!this.form) return;

        if (locked) {
          this.dataset.loading = 'true';
        } else {
          this.dataset.loading = 'false';
        }

        Array.from(this.form.elements).forEach((element) => {
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            element.readOnly = locked;
          }

          if (element instanceof HTMLButtonElement || element instanceof HTMLSelectElement) {
            element.disabled = locked;
          }
        });
      }

      /** @param {boolean} readOnly - Whether cart form text inputs should be read-only */
      setCartInputsReadOnly(readOnly) {
        if (!this.form) return;

        Array.from(this.form.elements).forEach((element) => {
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            element.readOnly = readOnly;
          }
        });
      }

      /**
       * @param {Event} e - The event object triggered by the change action
       * @returns {Promise<any>} - The response from the server
       */
      async changeHandler(e) {
        if (!this.form) return;

        const target = e.target;

        if (target instanceof HTMLSelectElement && target.hasAttribute('data-cart-variant-select')) {
          e.preventDefault();

          return this.variantChangeHandler(target);
        }

        if (this.isVariantChanging) return;

        e.preventDefault();

        if (this.controller) {
          this.controller.abort();
        }

        const operationId = ++this.cartOperationSeq;
        this.cartOperationId = operationId;
        this.dataset.loading = 'true';
        this.setCartInputsReadOnly(true);

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: new FormData(this.form),
          signal,
        };

        try {
          const response = await fetch(this.form.action, config).catch((error) => {
            if (error.name === 'AbortError') {
              return null;
            }

            throw error;
          });

          if (!response) return null;

          const data = await response.json();

          if (response.ok) {
            this.dataset.loading = 'true';

            await this.reloadSection();

            window.updateCartStateDisplays(data.item_count);

            return data;
          }

          throw new Error(data.description || 'Failed to update cart');
        } finally {
          if (this.cartOperationId === operationId) {
            this.controller = null;

            if (this.isConnected && !this.isVariantChanging) {
              this.dataset.loading = 'false';
              this.setCartInputsReadOnly(false);
            }
          }
        }
      }

      /**
       * @param {HTMLSelectElement} select - The size select element
       * @returns {number} - The current line item quantity
       */
      getLineQuantity(select) {
        const cartItem = select.closest('[data-cart-item]');
        const quantityInput = cartItem?.querySelector('input[name^="updates["]');

        if (quantityInput instanceof HTMLInputElement) {
          return Number(quantityInput.value);
        }

        return Number(select.dataset.quantity);
      }

      /**
       * @param {HTMLSelectElement} select - The size select element
       * @returns {Promise<any>} - The response from the server
       */
      async variantChangeHandler(select) {
        const lineKey = select.dataset.lineKey;
        const quantity = this.getLineQuantity(select);
        const newVariantId = Number(select.value);
        const currentVariantId = Number(select.dataset.currentVariantId);
        const previousVariantId = currentVariantId;

        if (!lineKey || !quantity || !newVariantId || newVariantId === currentVariantId) return;

        if (this.controller) {
          this.controller.abort();
        }

        if (this.variantController) {
          this.variantController.abort();
        }

        const operationId = ++this.variantOperationSeq;
        this.variantOperationId = operationId;
        this.isVariantChanging = true;
        this.variantController = new AbortController();
        const signal = this.variantController.signal;

        this.setFormLocked(true);

        try {
          /** @type {Record<string, string>} */
          let properties = {};

          if (select.dataset.properties) {
            try {
              properties = JSON.parse(select.dataset.properties);
            } catch {
              properties = {};
            }
          }

          const config = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            signal,
          };

          /** @type {{ id: number; quantity: number; properties?: Record<string, string> }} */
          const item = {
            id: newVariantId,
            quantity,
          };

          if (Object.keys(properties).length > 0) {
            item.properties = properties;
          }

          const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            ...config,
            body: JSON.stringify({
              items: [item],
            }),
          }).catch((error) => {
            if (error.name === 'AbortError') {
              return null;
            }

            throw error;
          });

          if (!addResponse) {
            select.value = String(previousVariantId);

            return null;
          }

          const data = await addResponse.json();

          if (!addResponse.ok) {
            select.value = String(previousVariantId);

            throw new Error(data.description || 'Failed to update cart item size');
          }

          const removeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            ...config,
            body: JSON.stringify({
              id: lineKey,
              quantity: 0,
            }),
          }).catch((error) => {
            if (error.name === 'AbortError') {
              return null;
            }

            throw error;
          });

          if (!removeResponse) {
            await rollbackVariantSizeChange(data, newVariantId, quantity, lineKey);

            return null;
          }

          if (!removeResponse.ok) {
            const rollbackData = await rollbackVariantSizeChange(data, newVariantId, quantity, lineKey);

            await this.reloadSection();

            window.updateCartStateDisplays(rollbackData?.item_count?.toString());
            select.value = String(previousVariantId);

            throw new Error('Failed to update cart item size');
          }

          this.dataset.loading = 'true';

          await this.reloadSection();

          this.dataset.loading = 'false';
          window.updateCartStateDisplays(data.item_count);

          return data;
        } finally {
          if (this.variantOperationId === operationId) {
            this.isVariantChanging = false;
            this.variantController = null;

            if (this.isConnected) {
              this.setFormLocked(false);
            }
          }
        }
      }

      /** @param {Event} e - The event object triggered by cart form clicks */
      clickHandler(e) {
        const target = e.target instanceof Element ? e.target : null;

        if (!target) return;

        if (this.isVariantChanging) return;

        const quantityStep = target.closest('[data-cart-quantity-step]');

        if (quantityStep instanceof HTMLButtonElement) {
          e.preventDefault();

          const cartItem = quantityStep.closest('[data-cart-item]');
          const quantityInput = cartItem?.querySelector('input[name^="updates["]');

          if (!quantityInput || !(quantityInput instanceof HTMLInputElement)) return;

          const min = Number(quantityInput.min) || 0;
          const max = Number(quantityInput.max) || 10;
          const currentQuantity = Number(quantityInput.value);

          if (quantityStep.dataset.cartQuantityStep === 'increase' && currentQuantity < max) {
            quantityInput.value = String(currentQuantity + 1);
          } else if (quantityStep.dataset.cartQuantityStep === 'decrease' && currentQuantity > min) {
            quantityInput.value = String(currentQuantity - 1);
          } else {
            return;
          }

          this.form?.dispatchEvent(new Event('change'));

          return;
        }

        if (target instanceof HTMLAnchorElement && target.closest('a') && target.href.includes('/cart/change')) {
          e.preventDefault();

          const quantityControl = target.closest('[data-cart-item]')?.querySelector('[name^="updates["]');

          if (quantityControl instanceof HTMLInputElement || quantityControl instanceof HTMLSelectElement) {
            quantityControl.value = '0';

            this.form?.dispatchEvent(new Event('change'));
          }
        }
      }

      async reloadSection() {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const config = {
          method: 'GET',
          signal,
        };

        const cartSectionUrl =
          location.pathname.endsWith('/cart') || location.pathname.endsWith('/cart/')
            ? `${window.Shopify.routes.root}cart?sections=section-main-cart`
            : `${window.Shopify.routes.root}?sections=section-main-cart`;

        const response = await fetch(cartSectionUrl, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (!response) return null;

        const data = await response.json();

        if (response.ok) {
          if (data['section-main-cart']) {
            const html = new DOMParser().parseFromString(data['section-main-cart'], 'text/html');
            const component = html.querySelector('cart-form');

            if (component) {
              window.__cartStateSignature = undefined;
              this.replaceWith(component);

              component.querySelectorAll('clerk-cart-cross-sell').forEach((element) => {
                customElements.upgrade(element);
              });
            }
          }

          return data;
        }

        throw new Error(response.statusText || 'Failed to reload cart section');
      }

      /**
       * @param {string[]} discounts - Discount codes to apply to the cart.
       * @returns {Promise<any | null>} Updated cart section data, or null when aborted.
       */
      async updateDiscounts(discounts) {
        const cookie = window.getCookie('cart');

        if (!cookie) return null;

        if (this.controller) {
          this.controller.abort();
        }

        this.dataset.loading = 'true';
        this.controller = new AbortController();
        const signal = this.controller.signal;

        const query = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
            cart {
              id
            }
            userErrors {
              field
              message
            }
            warnings {
              code
              message
            }
          }
        }`;

        const variables = {
          cartId: `gid://shopify/Cart/${decodeURIComponent(cookie)}`,
          discountCodes: discounts,
        };

        try {
          const data = await storefrontApi(query, variables, signal).catch((error) => {
            if (error.name === 'AbortError') {
              return null;
            }

            throw error;
          });

          if (!data) return null;

          const userErrors = data.cartDiscountCodesUpdate?.userErrors ?? [];

          if (userErrors.length > 0) {
            throw new Error(userErrors[0].message || 'Failed to update discounts');
          }

          return await this.reloadSection();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update discounts';
          throw new Error(message || 'Failed to update discounts');
        } finally {
          this.dataset.loading = 'false';
        }
      }
    },
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form[action$="/cart/add"]');

        this.options = this.querySelector('product-option-selector');

        /** @type {HTMLButtonElement | null} */
        this.submit = this.querySelector('[type="submit"]');
        this.controller = null;
      }

      connectedCallback() {
        if (this.form) {
          this.boundSubmitHandler = this.submitHandler.bind(this);
          this.boundChangeHandler = this.changeHandler.bind(this);

          this.form.addEventListener('submit', this.boundSubmitHandler);

          if (this.options) this.form.addEventListener('change', this.boundChangeHandler);
        }
      }

      disconnectedCallback() {
        if (this.form) {
          if (this.boundSubmitHandler) {
            this.form.removeEventListener('submit', this.boundSubmitHandler);
          }
          if (this.boundChangeHandler) {
            this.form.removeEventListener('change', this.boundChangeHandler);
          }
        }
      }

      /**
       * @param {Event} e - The event object triggered by the submit action
       * @returns {Promise<any>} - The response from the server
       */
      async submitHandler(e) {
        if (!this.form) return;

        e.preventDefault();

        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        if (this.submit) {
          this.submit.disabled = true;
          this.submit.dataset.loading = 'true';
        }

        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: new FormData(this.form),
          signal,
        };

        const response = await fetch(this.form.action, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response) {
          const data = await response.json();

          if (this.submit) {
            this.submit.disabled = false;
            this.submit.dataset.loading = 'false';
          }

          if (response.ok) {
            window.updateCartStateDisplays();

            const cartTray = window.traps.store['cart-tray'];

            if (location.pathname === '/cart' || (cartTray && cartTray.active)) {
              await document.querySelector('cart-form')?.reloadSection();
            } else {
              Object.values(window.traps.store).forEach((trap) => trap.deactivate());
            }

            if (cartTray && location.pathname !== '/cart') {
              cartTray.activate();
            }

            return data;
          }
        }

        throw new Error(response?.statusText || 'Failed to add to cart');
      }

      /**
       * @param {Event} e - The form submit event
       * @returns {Promise<any>} - The response from the server
       */
      async changeHandler(e) {
        const target = e.target;

        if (this.form && this.options && this.options instanceof HTMLElement && target instanceof HTMLElement) {
          const url =
            /** @type {HTMLInputElement | null} */
            (target.closest('[data-product-url]') ?? target.closest('[data-product-url]'))?.dataset.productUrl ||
            location.pathname;

          /** @type {HTMLInputElement | null} */
          const input = this.options.querySelector('input[type="radio"]:checked');

          const variant = input?.dataset.optionVariantId || null;

          if (!variant) {
            return null;
          }

          if (this.controller) {
            this.controller.abort();
          }

          this.dataset.loading = 'true';
          Array.from([this.form.elements, this.submit].flat()).forEach((element) => {
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
              element.readOnly = true;
            }

            if (element instanceof HTMLButtonElement) {
              element.disabled = true;
            }
          });

          this.controller = new AbortController();
          const signal = this.controller.signal;

          const config = {
            method: 'GET',
            signal,
          };

          const response = await fetch(`${url}?sections=section-main-product&variant=${variant}`, config).catch(
            (error) => {
              if (error.name === 'AbortError') {
                return null;
              }

              throw error;
            },
          );

          if (!response) {
            this.removeAttribute('data-loading');

            return null;
          }

          const data = await response.json();

          this.dataset.loading = 'false';
          Array.from([this.form.elements, this.submit].flat()).forEach((element) => {
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
              element.readOnly = false;
            }

            if (element instanceof HTMLButtonElement) {
              element.disabled = false;
            }
          });

          if (response.ok) {
            if (data['section-main-product']) {
              const html = new DOMParser().parseFromString(data['section-main-product'], 'text/html');
              const element = html.querySelector('.shopify-section product-form');

              if (element) {
                const id = target.closest('input[id]')?.id;

                this.replaceWith(element);

                if (id) {
                  const input = document.querySelector(`#${id}`);

                  if (input && input instanceof HTMLInputElement) {
                    input.focus();
                  }
                }
              }
            }

            return data;
          }

          throw new Error(data.description || 'Failed to change product option');
        }
      }
    },
  );
}

if (!customElements.get('product-form-compact')) {
  customElements.define(
    'product-form-compact',
    class ProductFormCompact extends HTMLElement {
      constructor() {
        super();

        this.controller = null;
      }

      connectedCallback() {
        this.boundClickHandler = (/** @type {MouseEvent} */ e) => {
          const target = e.target;

          if (target && target instanceof HTMLElement) {
            const closest = target.closest('[data-variant-id], [data-reveal-options]');

            if (closest && closest instanceof HTMLElement) {
              if (closest.hasAttribute('data-reveal-options')) {
                this.dataset.state = 'options';
              }

              if (closest.dataset.variantId) {
                this.submitHandler(closest);
              }
            }
          }
        };

        this.addEventListener('click', this.boundClickHandler);
      }

      disconnectedCallback() {
        if (this.boundClickHandler) {
          this.removeEventListener('click', this.boundClickHandler);
        }
      }

      /**
       * @param {HTMLElement} target - The target element
       * @returns {Promise<any>} - The response from the server
       */
      async submitHandler(target) {
        const variantId = target.dataset.variantId;

        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        this.dataset.state = 'loading';

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
              },
            ],
          }),
          signal,
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response) {
          const data = await response.json();

          if (response.ok) {
            this.dataset.state = 'success';

            window.updateCartStateDisplays();

            setTimeout(() => {
              this.dataset.state = 'idle';
            }, 2000);

            return data;
          }

          this.dataset.state = 'idle';

          // TODO: Show error message to the user, e.g. "Only 1 item was added to your cart due to availability"
          throw new Error(data?.description || response?.statusText || 'Failed to add to cart');
        }
      }
    },
  );
}

if (!customElements.get('cart-cross-sell-form')) {
  customElements.define(
    'cart-cross-sell-form',
    class CartCrossSellForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form[action$="/cart/add"]');

        /** @type {HTMLInputElement | null} */
        this.idInput = this.querySelector('input[name="id"]');

        /** @type {HTMLSelectElement | null} */
        this.sizeSelect = this.querySelector('[data-size-select]');

        this.controller = null;
      }

      connectedCallback() {
        this.boundSubmitHandler = this.submitHandler.bind(this);
        this.boundSizeSelectHandler = this.sizeSelectHandler.bind(this);

        this.form?.addEventListener('submit', this.boundSubmitHandler);
        this.sizeSelect?.addEventListener('change', this.boundSizeSelectHandler);
      }

      disconnectedCallback() {
        if (this.form && this.boundSubmitHandler) {
          this.form.removeEventListener('submit', this.boundSubmitHandler);
        }

        if (this.sizeSelect && this.boundSizeSelectHandler) {
          this.sizeSelect.removeEventListener('change', this.boundSizeSelectHandler);
        }
      }

      sizeSelectHandler() {
        if (!this.sizeSelect || !this.idInput) return;

        const variantId = this.sizeSelect.value;

        if (!variantId) return;

        this.idInput.value = variantId;
        this.submitHandler();
      }

      /**
       * @param {Event} [e] - The event object triggered by the submit action
       * @returns {Promise<any>} - The response from the server
       */
      async submitHandler(e) {
        if (!this.form) return;

        if (e) e.preventDefault();

        if (!this.idInput?.value) return;

        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        this.dataset.loading = 'true';

        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: new FormData(this.form),
          signal,
        };

        const response = await fetch(this.form.action, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response) {
          const data = await response.json();

          this.dataset.loading = 'false';

          if (response.ok) {
            window.updateCartStateDisplays();

            const cartTray = window.traps.store['cart-tray'];

            if (location.pathname === '/cart' || (cartTray && cartTray.active)) {
              await document.querySelector('cart-form')?.reloadSection();
            } else {
              Object.values(window.traps.store).forEach((trap) => trap.deactivate());
            }

            if (cartTray && location.pathname !== '/cart') {
              cartTray.activate();
            }

            return data;
          }
        }

        this.dataset.loading = 'false';

        throw new Error(response?.statusText || 'Failed to add to cart');
      }
    },
  );
}

if (!customElements.get('product-bundle-form')) {
  customElements.define(
    'product-bundle-form',
    class ProductBundleForm extends HTMLElement {
      constructor() {
        super();

        this.controller = null;

        /** @type {{[key: string]: string | null}} */
        this.selectedVariants = {
          1: null,
          2: null,
          3: null,
          4: null,
        };

        /** @type {number} */
        this.productCount = 2;

        /** @type {HTMLButtonElement | null} */
        this.submitButton = this.querySelector('[data-bundle-submit]');

        /** @type {HTMLSpanElement | null} */
        this.submitLabel = this.querySelector('[data-submit-label]');

        /** @type {HTMLInputElement | null} */
        this.bundleTimestamp = this.querySelector('[data-bundle-timestamp]');
      }

      connectedCallback() {
        this.productCount = Number(this.dataset.productCount) || 2;

        this.boundClickHandler = (/** @type {MouseEvent} */ e) => {
          const target = e.target;

          if (target && target instanceof HTMLElement) {
            const variantButton = target.closest('[data-variant-id]');
            const submitButton = target.closest('[data-bundle-submit]');

            if (variantButton && variantButton instanceof HTMLElement) {
              this._handleVariantSelection(variantButton);
            }

            if (submitButton) {
              this._submitHandler();
            }
          }
        };

        this.addEventListener('click', this.boundClickHandler);
      }

      disconnectedCallback() {
        if (this.boundClickHandler) {
          this.removeEventListener('click', this.boundClickHandler);
        }
      }

      /**
       * @param {HTMLElement} button - The variant button that was clicked
       */
      _handleVariantSelection(button) {
        const container = button.closest('[data-product-variants]');

        if (!container || !(container instanceof HTMLElement)) return;

        const productIndex = container.dataset.productVariants;
        const variantId = button.dataset.variantId;

        const allButtons = container.querySelectorAll('[data-variant-id]');

        allButtons.forEach((btn) => {
          if (btn instanceof HTMLElement) {
            btn.removeAttribute('aria-selected');
          }
        });

        button.setAttribute('aria-selected', 'true');

        if (productIndex) {
          this.selectedVariants[productIndex] = variantId || null;
        }

        this._updateSubmitButton();
      }

      _updateSubmitButton() {
        if (!this.submitButton) return;

        const allVariantsSelected = this._areAllVariantsSelected();

        if (allVariantsSelected && this.submitLabel) {
          this.submitButton.disabled = false;
          this.submitLabel.textContent = this.dataset.labelAddToCart || 'Add to cart';
        } else {
          this.submitButton.disabled = true;
        }
      }

      /**
       * Check if all required variants are selected based on product count
       * @returns {boolean} True if all variants are selected
       */
      _areAllVariantsSelected() {
        for (let i = 1; i <= this.productCount; i++) {
          if (!this.selectedVariants[i]) {
            return false;
          }
        }

        return true;
      }

      /**
       * Get array of selected variant IDs
       * @returns {string[]} Array of selected variant IDs
       */
      _getSelectedVariantIds() {
        /** @type {string[]} */
        const variantIds = [];

        for (let i = 1; i <= this.productCount; i++) {
          const variantId = this.selectedVariants[i];

          if (variantId) {
            variantIds.push(variantId);
          }
        }

        return variantIds;
      }

      async _submitHandler() {
        if (!this._areAllVariantsSelected() || !this.bundleTimestamp) return;

        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        this.dataset.state = 'loading';

        if (this.submitButton) {
          this.submitButton.disabled = true;
        }

        const bundleId = new Date().getTime();
        const variantIds = this._getSelectedVariantIds();

        /** @type {{ id: number; quantity: number; properties?: { _bundle?: number } }[]} */
        const items = variantIds.map((variantId) => ({
          id: Number(variantId),
          quantity: 1,
          properties: {
            _bundle: bundleId,
          },
        }));

        const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json());

        const giftWrappingVariantId = '54598013550971';

        if (
          cart.items.length === 0 ||
          cart.items.some((/** @type {any} */ item) => item.variant_id !== giftWrappingVariantId)
        ) {
          items.push({
            id: Number(giftWrappingVariantId),
            quantity: 1,
          });
        }

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
          signal,
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, config).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response) {
          const data = await response.json();

          if (response.ok) {
            window.updateCartStateDisplays();

            Object.values(window.traps.store).forEach((trap) => trap.deactivate());

            const cartTray = window.traps.store['cart-tray'];

            if (cartTray) {
              cartTray.activate();
            }

            setTimeout(() => {
              if (this.submitButton) {
                this.submitButton.disabled = false;
              }
            }, 2000);

            return data;
          }

          if (this.submitButton) {
            this.submitButton.disabled = false;
          }

          throw new Error(data?.description || response?.statusText || 'Failed to add bundle to cart');
        }
      }
    },
  );
}

if (!customElements.get('animated-details')) {
  customElements.define(
    'animated-details',
    class AnimatedDetails extends HTMLElement {
      constructor() {
        super();

        /** @type {number} */
        this.duration =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--transition-short')) * 1000 || 250;

        /** @type {HTMLDetailsElement | null} */
        this.details = this.querySelector('details');

        /** @type {HTMLElement | null} */
        this.summary = this.querySelector('summary');

        /** @type {Element | null | undefined} */
        this.content = this.details?.lastElementChild;

        /** @type {Animation | null} */
        this.animation = null;

        /** @type {boolean} */
        this.closing = false;

        /** @type {boolean} */
        this.expanding = false;

        /** @type {boolean} - Closes all sibling details within parent container when opening */
        this.exclusive = this.hasAttribute('exclusive');

        /** @type {boolean} - Opens the details element when the viewport matches the desktop media query */
        this.desktopOpen = this.hasAttribute('desktop-open');

        /** @type {string | null} */
        this.detailsId = this.getAttribute('data-details');

        /** @type {MediaQueryList | null} */
        this.desktopMediaQuery = null;
      }

      connectedCallback() {
        this.summary?.addEventListener('click', this.handleSummaryClick);

        if (this.desktopOpen) {
          this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
          this.handleMediaQueryChange(this.desktopMediaQuery.matches);

          if (this.desktopMediaQuery.addListener) {
            this.desktopMediaQuery.addListener(this.handleMediaQueryChangeEvent);
          } else {
            this.desktopMediaQuery.addEventListener('change', this.handleMediaQueryChangeEvent);
          }
        }

        window.addEventListener('click', this.handleGlobalClick);
      }

      disconnectedCallback() {
        this.summary?.removeEventListener('click', this.handleSummaryClick);
        window.removeEventListener('click', this.handleGlobalClick);

        if (this.desktopMediaQuery) {
          if (this.desktopMediaQuery.removeListener) {
            this.desktopMediaQuery.removeListener(this.handleMediaQueryChangeEvent);
          } else {
            this.desktopMediaQuery.removeEventListener('change', this.handleMediaQueryChangeEvent);
          }

          this.desktopMediaQuery = null;
        }

        if (this.animation) {
          this.animation.cancel();
          this.animation = null;
        }
      }

      /** @param {MouseEvent} e - The event object triggered by the summary click action */
      handleSummaryClick = (e) => {
        const target = e.target;

        if (!target) return;

        if (target instanceof HTMLInputElement) return;

        e.preventDefault();

        if (!this.desktopOpen || (this.desktopMediaQuery && !this.desktopMediaQuery.matches)) {
          this.toggle();
        }
      };

      /** @param {MediaQueryListEvent} e - The media query change event */
      handleMediaQueryChangeEvent = (e) => {
        this.handleMediaQueryChange(e.matches);
      };

      /** @param {boolean} matches - The state of the media query */
      handleMediaQueryChange(matches) {
        if (!this.details) return;

        if (matches) {
          this.details.open = true;

          if (this.summary) {
            this.summary.tabIndex = -1;
          }
        } else {
          this.details.open = false;

          if (this.summary) {
            this.summary.tabIndex = 0;
          }
        }
      }

      toggle() {
        if (this.closing || !this.details?.open) {
          this.expand();
        } else if (this.expanding || this.details?.open) {
          this.shrink();
        }
      }

      expand() {
        if (!this.details) return;

        this.expanding = true;
        this.details.style.overflow = 'hidden';
        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;

        window.requestAnimationFrame(() => {
          if (this.details && this.summary && this.content && this.content instanceof HTMLElement) {
            const startHeight = `${this.details.offsetHeight}px`;
            const endHeight = `${this.summary?.offsetHeight + (this.content.offsetHeight || 0)}px`;

            if (this.animation) {
              this.animation.cancel();
            }

            this.animation = this.details.animate(
              {
                height: [startHeight, endHeight],
              },
              {
                duration: this.duration,
                easing: 'ease',
              },
            );

            this.animation.onfinish = () => this.onAnimationFinish(true);
            this.animation.oncancel = () => (this.expanding = false);

            if (this.exclusive) {
              const siblings = this.parentElement?.querySelectorAll('animated-details:has(details[open])') || [];

              siblings.forEach((sibling) => {
                if (sibling !== this && sibling instanceof AnimatedDetails) {
                  sibling.shrink();
                }
              });
            }
          }
        });
      }

      shrink() {
        if (!this.details) return;

        this.closing = true;
        this.details.style.overflow = 'hidden';

        const startHeight = `${this.details.offsetHeight}px`;
        const endHeight = `${this.summary?.offsetHeight}px`;

        if (this.animation) {
          this.animation.cancel();
        }

        this.animation = this.details.animate(
          {
            height: [startHeight, endHeight],
          },
          {
            duration: this.duration,
            easing: 'ease',
          },
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.closing = false);
      }

      /** @param {boolean} open - The state of the element */
      onAnimationFinish(open) {
        if (!this.details) return;

        this.details.open = open;
        this.animation = null;
        this.closing = false;
        this.expanding = false;
        this.details.style.height = this.details.style.overflow = '';
      }

      /** @param {Event} e - The event object triggered by the click action */
      handleGlobalClick = (e) => {
        if (e.target instanceof HTMLElement && e.target.closest('[data-details-id]')) {
          /** @type {string | null | undefined} */
          const details = e.target.closest('[data-details-id]')?.getAttribute('data-details-id');

          if (details === this.detailsId) {
            this.scrollIntoView({
              block: 'center',
            });
            this.expand();
          }
        }
      };
    },
  );
}

if (!customElements.get('animated-tabs')) {
  customElements.define(
    'animated-tabs',
    class AnimatedTabs extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.list = this.querySelector('[data-tabs-list]');

        /** @type {HTMLElement[]} */
        this.panels = Array.from(this.querySelectorAll(':scope > [data-panels-list] > [data-tabs-panel]'));

        /** @type {HTMLButtonElement[]} */
        this.tabs = [];
      }

      connectedCallback() {
        if (this.list && this.panels) {
          this.tabs = Array.from(this.list.querySelectorAll('button[data-tab-id]'));

          this.list.setAttribute('role', 'tablist');
          this.tabs.forEach((tab, idx) => {
            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
            tab.setAttribute('tabindex', idx === 0 ? '0' : '-1');

            const li = tab.closest('li');
            if (li) li.setAttribute('role', 'presentation');
          });

          this.panels.forEach((panel, idx) => {
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('tabindex', '-1');
            if (idx !== 0) panel.setAttribute('hidden', 'true');
          });

          this.addEventListener('click', this.handleClick);
          this.addEventListener('keydown', this.handleKeydown);
          window.addEventListener('click', this.handleGlobalClick);
        }
      }

      /** @param {Event} e - The event object triggered by the click action */
      handleClick = (e) => {
        if (e.target instanceof HTMLElement) {
          /** @type {HTMLButtonElement | null} */
          const tab = e.target.closest('[data-tab-id]');

          if (tab && this.tabs.includes(tab)) {
            e.preventDefault();
            this.switch(tab.dataset.tabId);
          }
        }
      };

      /** @param {KeyboardEvent} e - The event object triggered by the keydown action */
      handleKeydown = (e) => {
        const target = e.target;

        if (target instanceof HTMLElement) {
          if (target instanceof HTMLButtonElement && !this.tabs.includes(target)) return;

          const component = target.closest('animated-tabs');

          if (component !== this) return;

          /** @type {HTMLButtonElement | null} */
          const tab = target.closest('button[data-tab-id]');

          if (!tab || !this.tabs.includes(tab)) return;

          switch (e.key) {
            case 'ArrowLeft':
              this.prev();
              break;
            case 'ArrowRight':
              this.next();
              break;
            case 'Home':
              e.preventDefault();
              this.switch(this.tabs[0].dataset.tabId);
              break;
            case 'End':
              e.preventDefault();
              this.switch(this.tabs[this.tabs.length - 1].dataset.tabId);
              break;
          }
        }
      };

      /** @param {Event} e - The event object triggered by the click action */
      handleGlobalClick = (e) => {
        if (e.target instanceof HTMLElement && e.target.closest('[data-tab-id]')) {
          /** @type {HTMLElement | null} */
          const tab = e.target.closest('[data-tab-id]');

          if (tab) {
            this.switch(tab.dataset.tabId);
          }
        }
      };

      prev() {
        const currentIndex = this.tabs.findIndex((t) => t === document.activeElement);
        const newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;

        if (this.tabs[newIndex].dataset.tabId) this.switch(this.tabs[newIndex].dataset.tabId);
      }

      next() {
        const currentIndex = this.tabs.findIndex((t) => t === document.activeElement);
        const newIndex = (currentIndex + 1) % this.tabs.length;
        if (this.tabs[newIndex].dataset.tabId) this.switch(this.tabs[newIndex].dataset.tabId);
      }

      /**
       *  @param {string | undefined} id - The new tab to switch to.
       */
      switch(id) {
        if (!id) return;

        const tab = this.tabs.find((t) => t.dataset.tabId === id);
        const panel = this.panels.find((p) => p.id === id);
        if (!tab || !panel) return;

        this.tabs.forEach((t) => {
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });

        this.panels.forEach((p) => (p.hidden = true));

        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
        panel.hidden = false;
        tab.focus();
      }
    },
  );
}

(function openExternalLinksInNewTab() {
  Array.from(document.links).forEach((link) => {
    if (link.hostname !== window.location.hostname) {
      link.target = '_blank';
      link.rel = 'noreferrer noopener';
    }
  });
})();

(function scrollFade() {
  const items = document.querySelectorAll('[data-scroll-fade]');

  items.forEach((item) => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (item instanceof HTMLElement) {
            if (entry.isIntersecting) {
              item.dataset.scrollFade = 'true';

              observer.unobserve(item);
            } else {
              item.dataset.scrollFade = 'false';
            }
          }
        });
      },
      {
        threshold: 0.05,
      },
    );

    observer.observe(item);
  });
})();

/**
 * @param {string} query - Storefront GraphQL query.
 * @param {Record<string, unknown> | undefined} [variables] - Query variables.
 * @param {AbortSignal | undefined} [signal] - Optional abort signal.
 * @returns {Promise<any>} GraphQL response data.
 */
async function storefrontApi(query, variables = undefined, signal = undefined) {
  const response = await fetch(`https://${window.Shopify.shop}/api/2026-04/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': 'b527703b9461f12285616713994d2586',
    },
    body: JSON.stringify({ query, variables }),
    signal,
  });

  if (!response.ok) throw new Error(`HTTP error ${response.status}`);

  const json = await response.json();

  if (json.errors) throw new Error('GraphQL error');

  return json.data;
}

/** @returns {Promise<number>} Exchange rate multiplier for active market. */
// eslint-disable-next-line no-unused-vars -- wired up by rewards panel
async function getExchangeRateMultiplier() {
  const cached = window.__exchangeRateMultiplier;

  if (cached) return cached;

  const country = window.Shopify.country;

  const baseQuery = `
    query ShopBaseCurrencyRateMetafield {
      shop {
        baseCurrencyRate: metafield(namespace: "custom", key: "base_currency_rate") {
          value
        }
      }
    }
  `;

  const marketQuery = `
    query ShopBaseCurrencyRateMetafield($country: CountryCode!)
    @inContext(country: $country) {
      shop {
        baseCurrencyRate: metafield(namespace: "custom", key: "base_currency_rate") {
          value
        }
      }
    }
  `;

  /**
   * @param {any} data - Storefront GraphQL shop payload.
   * @returns {number | null} Parsed metafield amount.
   */
  function parseAmount(data) {
    const value = data?.shop?.baseCurrencyRate?.value;

    if (!value) return null;

    return Number.parseFloat(JSON.parse(value).amount);
  }

  const [baseData, marketData] = await Promise.all([storefrontApi(baseQuery), storefrontApi(marketQuery, { country })]);

  const baseAmount = parseAmount(baseData);
  const marketAmount = parseAmount(marketData);

  if (!baseAmount || !marketAmount) return 1;

  const multiplier = Math.round((marketAmount / baseAmount) * 1000) / 1000 || 1;

  window.__exchangeRateMultiplier = multiplier;

  return multiplier;
}
